import React, { useState } from 'react';

/**
 * SheetTabBar — renders the row of sheet tabs for the model-config editor.
 *
 * Each tab can be:
 *   • clicked to switch the active sheet
 *   • double-clicked (or pencil) to rename
 *   • removed via the trash icon
 *
 * A trailing "+" button lets the user add a new sheet.
 *
 * Props:
 *   sheets       : [{ key, name }]
 *   activeKey    : key of the currently selected sheet
 *   markers      : { [key]: boolean }   — show an "edited" dot on the tab when true
 *   onSelect     : (key) => void
 *   onRename     : (key, newName) => void
 *   onAdd        : (name) => void
 *   onDelete     : (key) => void
 */
export default function SheetTabBar({
  sheets,
  activeKey,
  markers = {},
  onSelect,
  onRename,
  onAdd,
  onDelete,
}) {
  const [editingKey, setEditingKey] = useState(null);
  const [draft, setDraft] = useState('');
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');

  const startRename = (sheet) => {
    setEditingKey(sheet.key);
    setDraft(sheet.name);
  };

  const commitRename = () => {
    const v = draft.trim();
    if (v && editingKey) onRename(editingKey, v);
    setEditingKey(null);
  };

  const submitAdd = () => {
    const v = newName.trim();
    if (v) onAdd(v);
    setNewName('');
    setAdding(false);
  };

  return (
    <ul className="nav nav-tabs mb-0" style={{ flexWrap: 'wrap' }}>
      {sheets.map((s) => {
        const isActive = s.key === activeKey;
        const isEditing = editingKey === s.key;
        return (
          <li key={s.key} className="nav-item">
            <div
              className={`nav-link d-inline-flex align-items-center gap-1${isActive ? ' active' : ''}`}
              style={{ fontSize: 13, cursor: isEditing ? 'text' : 'pointer', padding: '6px 10px' }}
              onClick={() => !isEditing && onSelect(s.key)}
              onDoubleClick={(e) => { e.stopPropagation(); startRename(s); }}
              title={isEditing ? '' : `Sheet key: ${s.key}`}
            >
              {isEditing ? (
                <input
                  autoFocus
                  value={draft}
                  onChange={e => setDraft(e.target.value)}
                  onBlur={commitRename}
                  onClick={e => e.stopPropagation()}
                  onKeyDown={e => {
                    if (e.key === 'Enter') commitRename();
                    if (e.key === 'Escape') setEditingKey(null);
                  }}
                  style={{
                    fontSize: 13, padding: '1px 4px', border: '1px solid #0d6efd',
                    borderRadius: 3, width: Math.max(80, draft.length * 8),
                  }}
                />
              ) : (
                <>
                  <span>{s.name}</span>
                  {markers[s.key] && (
                    <span style={{ color: '#f0a500', fontSize: 11 }}>●</span>
                  )}
                </>
              )}
              {isActive && !isEditing && (
                <>
                  <button
                    onClick={(e) => { e.stopPropagation(); startRename(s); }}
                    title="Rename sheet"
                    style={{ background: 'none', border: 'none', padding: '0 2px', cursor: 'pointer', color: '#0d6efd', fontSize: 11, opacity: 0.7 }}
                  >
                    <i className="bi bi-pencil" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); onDelete(s.key); }}
                    title="Remove sheet"
                    style={{ background: 'none', border: 'none', padding: '0 2px', cursor: 'pointer', color: '#dc3545', fontSize: 11, opacity: 0.7 }}
                  >
                    <i className="bi bi-x-lg" />
                  </button>
                </>
              )}
            </div>
          </li>
        );
      })}
      <li className="nav-item d-flex align-items-center">
        {adding ? (
          <div className="d-flex align-items-center gap-1 ms-2">
            <input
              autoFocus
              value={newName}
              onChange={e => setNewName(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') submitAdd();
                if (e.key === 'Escape') { setAdding(false); setNewName(''); }
              }}
              placeholder="Sheet name…"
              style={{ fontSize: 13, padding: '2px 6px', border: '1px solid #0d6efd', borderRadius: 3, width: 140 }}
            />
            <button className="btn btn-primary btn-sm py-0 px-2" style={{ fontSize: 11 }} onClick={submitAdd} disabled={!newName.trim()}>Add</button>
            <button className="btn btn-outline-secondary btn-sm py-0 px-2" style={{ fontSize: 11 }} onClick={() => { setAdding(false); setNewName(''); }}>✕</button>
          </div>
        ) : (
          <button
            className="btn btn-link btn-sm ms-2"
            style={{ fontSize: 13, textDecoration: 'none' }}
            onClick={() => setAdding(true)}
            title="Add a new sheet"
          >
            <i className="bi bi-plus-lg me-1" />Add sheet
          </button>
        )}
      </li>
    </ul>
  );
}
